import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import MobileLayout from '../components/MobileLayout';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

interface Companion {
  id: string;
  name: string;
  gender: string;
  personality: string;
  avatarUrl?: string;
  isActive: boolean;
  lastMessageAt?: string;
  messageCount?: number;
}

const Companions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [companions, setCompanions] = useState<Companion[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCompanionId, setActiveCompanionId] = useState<string | null>(null);

  useEffect(() => {
    fetchCompanions();
    // Get active companion from localStorage
    const storedActiveId = localStorage.getItem('activeCompanionId');
    setActiveCompanionId(storedActiveId);
  }, []);

  const fetchCompanions = async () => {
    try {
      const response = await axios.get('/companions');
      setCompanions(response.data);
    } catch (error) {
      console.error('Failed to fetch companions:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectCompanion = (companionId: string) => {
    localStorage.setItem('activeCompanionId', companionId);
    navigate(`/chat/${companionId}`);
  };

  const getEmojiAvatar = (avatarUrl?: string) => {
    // If avatarUrl is an emoji, use it directly
    if (avatarUrl && avatarUrl.length <= 4) {
      return avatarUrl;
    }
    // Default emojis based on gender or random
    return '🌟';
  };

  const getAvatarBg = (index: number) => {
    const colors = [
      'bg-purple-500',
      'bg-blue-500',
      'bg-pink-500',
      'bg-green-500',
      'bg-indigo-500',
      'bg-teal-500',
    ];
    return colors[index % colors.length];
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
            {t('companions.loading', 'Loading companions...')}
          </div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-dark-border dark:border-dark-border border-light-border">
          <h1 className="text-xl font-semibold text-dark-text dark:text-dark-text text-light-text text-center">
            {t('companions.title', 'My Companions')}
          </h1>
        </div>

        {/* Companions List */}
        <div className="flex-1 overflow-y-auto p-4">
          {companions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="w-24 h-24 bg-dark-surface dark:bg-dark-surface bg-light-surface rounded-full flex items-center justify-center mb-4">
                <SparklesIcon className="w-12 h-12 text-primary-500" />
              </div>
              <h2 className="text-lg font-semibold text-dark-text dark:text-dark-text text-light-text mb-2">
                {t('companions.noCompanions', 'No Companions Yet')}
              </h2>
              <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-center mb-6">
                {t('companions.createFirst', 'Create your first AI companion to start chatting')}
              </p>
              <button
                onClick={() => navigate('/companion/name')}
                className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white rounded-xl font-medium transition-colors"
              >
                {t('companions.create', 'Create Companion')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {companions.map((companion, index) => (
                <button
                  key={companion.id}
                  onClick={() => selectCompanion(companion.id)}
                  className={`w-full p-4 rounded-2xl transition-all ${
                    activeCompanionId === companion.id
                      ? 'bg-primary-500/20 border-2 border-primary-500'
                      : 'bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-surface/80 dark:hover:bg-dark-surface/80 hover:bg-light-surface/80 border-2 border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    {/* Avatar */}
                    <div
                      className={`w-14 h-14 rounded-full flex items-center justify-center text-2xl ${getAvatarBg(
                        index,
                      )}`}
                    >
                      {getEmojiAvatar(companion.avatarUrl)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <h3 className="text-dark-text dark:text-dark-text text-light-text font-semibold">
                          {companion.name}
                        </h3>
                        {companion.isActive && (
                          <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                            {t('companions.active', 'Active')}
                          </span>
                        )}
                      </div>
                      <p className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary text-sm line-clamp-1">
                        {companion.personality}
                      </p>
                      {companion.messageCount !== undefined && companion.messageCount > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                          <ChatBubbleLeftRightIcon className="w-3 h-3 text-dark-text-tertiary dark:text-dark-text-tertiary text-light-text-tertiary" />
                          <span className="text-dark-text-tertiary dark:text-dark-text-tertiary text-light-text-tertiary text-xs">
                            {t('companions.messageCount', '{{count}} messages', {
                              count: companion.messageCount,
                            })}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Arrow */}
                    <div className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary">
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}

              {/* Add New Companion Button */}
              <button
                onClick={() => navigate('/companion/name')}
                className="w-full p-4 rounded-2xl bg-dark-surface dark:bg-dark-surface bg-light-surface hover:bg-dark-surface/80 dark:hover:bg-dark-surface/80 hover:bg-light-surface/80 border-2 border-dashed border-dark-border dark:border-dark-border border-light-border transition-all"
              >
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-500/20 flex items-center justify-center">
                    <PlusIcon className="w-5 h-5 text-primary-500" />
                  </div>
                  <span className="text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary font-medium">
                    {t('companions.createNew', 'Create New Companion')}
                  </span>
                </div>
              </button>
            </div>
          )}
        </div>
      </div>
    </MobileLayout>
  );
};

export default Companions;
